
import { useEffect, useMemo, useState } from "react";
import { formatDate } from "../../../utils/formatDate";

const PlanShiftList = ({
    setups = [],
    loading,
    onEdit,
    onDelete
}) => {

    // =========================================================
    // SEARCH
    // =========================================================

    const [searchTerm, setSearchTerm] = useState("");


    // =========================================================
    // SORTING - PRODUCT
    // =========================================================

    const [sortOrder, setSortOrder] = useState("asc");


    // =========================================================
    // PAGINATION
    // =========================================================

    const [currentPage, setCurrentPage] = useState(1);

    const [pageSize, setPageSize] = useState(5);


    // =========================================================
    // SEARCH + SORT
    // =========================================================

    const filteredAndSortedSetups = useMemo(() => {

        let result = setups.filter((setup) => {

            const search =
                searchTerm.toLowerCase().trim();

            if (!search) {
                return true;
            }

            return (

                String(setup.id ?? "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(setup.machine_code ?? "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(setup.product_name ?? "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(setup.die_type ?? "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(setup.shift_name ?? "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(setup.plan_date ?? "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(setup.notes ?? "")
                    .toLowerCase()
                    .includes(search)

                ||

                String(setup.status ?? "")
                    .toLowerCase()
                    .includes(search)

            );

        });


        // =====================================================
        // SORT BY PRODUCT
        // =====================================================

        result.sort((a, b) => {

            const productA =
                String(a.product_name ?? "")
                    .toLowerCase();

            const productB =
                String(b.product_name ?? "")
                    .toLowerCase();

            const comparison =
                productA.localeCompare(
                    productB,
                    undefined,
                    {
                        numeric: true,
                        sensitivity: "base"
                    }
                );

            return sortOrder === "asc"
                ? comparison
                : -comparison;

        });


        return result;

    }, [
        setups,
        searchTerm,
        sortOrder
    ]);


    // =========================================================
    // TOTAL PAGES
    // =========================================================

    const totalPages = Math.ceil(
        filteredAndSortedSetups.length /
        pageSize
    );


    // =========================================================
    // CURRENT PAGE DATA
    // =========================================================

    const currentRecords = useMemo(() => {

        const startIndex =
            (currentPage - 1) * pageSize;

        const endIndex =
            startIndex + pageSize;

        return filteredAndSortedSetups.slice(
            startIndex,
            endIndex
        );

    }, [
        filteredAndSortedSetups,
        currentPage,
        pageSize
    ]);


    // =========================================================
    // RESET PAGE WHEN SEARCH / SORT CHANGES
    // =========================================================

    useEffect(() => {

        setCurrentPage(1);

    }, [
        searchTerm,
        sortOrder
    ]);


    // =========================================================
    // PAGE SIZE CHANGE
    // =========================================================

    const handlePageSizeChange = (e) => {

        const newSize =
            Number(e.target.value);

        setPageSize(newSize);

        setCurrentPage(1);

    };


    // =========================================================
    // PAGE CHANGE
    // =========================================================

    const handlePageChange = (page) => {

        if (
            page < 1 ||
            page > totalPages
        ) {
            return;
        }

        setCurrentPage(page);

    };


    // =========================================================
    // PRODUCT SORT
    // =========================================================

    const handleProductSort = () => {

        setSortOrder((previousOrder) =>
            previousOrder === "asc"
                ? "desc"
                : "asc"
        );

    };


    // =========================================================
    // PAGE NUMBERS
    // =========================================================

    const pageNumbers = [];

    for (
        let i = 1;
        i <= totalPages;
        i++
    ) {

        pageNumbers.push(i);

    }


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div className="card">

            <div className="card-body">


                {/* =================================================
                    HEADER
                ================================================= */}

                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">


                    {/* SEARCH */}

                    <div
                        style={{
                            width: "400px",
                            maxWidth: "100%"
                        }}
                    >

                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search Item / Machine / Shift..."
                            value={searchTerm}
                            onChange={(e) =>
                                setSearchTerm(
                                    e.target.value
                                )
                            }
                        />

                    </div>


                    {/* PAGE SIZE */}

                    <div className="d-flex align-items-center gap-2">

                        <label className="mb-0">
                            Show
                        </label>

                        <select
                            className="form-select form-select-sm"
                            style={{
                                width: "80px"
                            }}
                            value={pageSize}
                            onChange={
                                handlePageSizeChange
                            }
                        >

                            <option value={5}>
                                5
                            </option>

                            <option value={10}>
                                10
                            </option>

                            <option value={25}>
                                25
                            </option>

                            <option value={50}>
                                50
                            </option>

                        </select>

                        <span>
                            entries
                        </span>

                    </div>

                </div>


                {/* =================================================
                    LOADING
                ================================================= */}

                {loading ? (

                    <div className="text-center py-5">

                        <div
                            className="spinner-border text-primary"
                            role="status"
                        />

                        <div className="mt-1">
                            Loading product machine setups...
                        </div>

                    </div>

                ) : filteredAndSortedSetups.length === 0 ? (

                    /* =================================================
                        NO DATA
                    ================================================= */

                    <div className="text-center text-muted py-5">

                        {searchTerm
                            ? "No matching records found."
                            : "No product machine setups found."
                        }

                    </div>

                ) : (

                    <>


                        {/* =================================================
                            TABLE
                        ================================================= */}

                        <div className="table-responsive">

                            <table className="table table-bordered table-hover align-middle">

                                <thead className="table-light">

                                    <tr>

                                        <th
                                            style={{
                                                width: "60px"
                                            }}
                                        >
                                            #
                                        </th>


                                        <th>
                                            Machine
                                        </th>


                                        {/* =================================
                                            SORTABLE PRODUCT
                                        ================================= */}

                                        <th
                                            onClick={
                                                handleProductSort
                                            }
                                            style={{
                                                cursor: "pointer",
                                                userSelect: "none"
                                            }}
                                            title="Sort by Product"
                                        >

                                            Item{" "}

                                            {sortOrder === "asc"
                                                ? "▲"
                                                : "▼"
                                            }

                                        </th>


                                        <th>
                                            Cavity
                                        </th>


                                        <th>
                                            Shift
                                        </th>


                                        <th>
                                            Plan Date
                                        </th>


                                        <th>
                                            Notes
                                        </th>


                                        <th>
                                            Status
                                        </th>


                                        <th
                                            className="text-center"
                                            style={{
                                                width: "180px"
                                            }}
                                        >
                                            Action
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {currentRecords.map(
                                        (setup, index) => {

                                            const rowNumber =
                                                (
                                                    (currentPage - 1)
                                                    * pageSize
                                                )
                                                +
                                                index
                                                +
                                                1;


                                            return (

                                                <tr
                                                    key={
                                                        setup.id
                                                    }
                                                >


                                                    {/* #
                                                    ================= */}

                                                    <td>
                                                        {rowNumber}
                                                    </td>


                                                    {/* MACHINE
                                                    ================= */}

                                                    <td>
                                                        {
                                                            setup.machine_code ||
                                                            "-"
                                                        }
                                                    </td>


                                                    {/* PRODUCT
                                                    ================= */}

                                                    <td>
                                                        {
                                                            setup.product_name ||
                                                            "-"
                                                        }
                                                    </td>


                                                    {/* DIE TYPE
                                                    ================= */}

                                                    <td>
                                                        {
                                                            setup.die_type ||
                                                            "-"
                                                        }
                                                    </td>


                                                    {/* SHIFT
                                                    ================= */}

                                                    <td>
                                                        {
                                                            setup.shift_name ||
                                                            "-"
                                                        }
                                                    </td>


                                                    {/* PLAN DATE
                                                    ================= */}

                                                    <td>

                                                        {setup.plan_date
                                                            ? formatDate(
                                                                setup.plan_date
                                                            )
                                                            : "-"
                                                        }

                                                    </td>


                                                    {/* NOTES
                                                    ================= */}

                                                    <td>
                                                        {
                                                            setup.notes ||
                                                            "-"
                                                        }
                                                    </td>


                                                    {/* STATUS
                                                    ================= */}

                                                    <td>

                                                        {setup.status ===
                                                            "saved" ? (

                                                            <span className="badge bg-success">
                                                                Saved
                                                            </span>

                                                        ) : (

                                                            <span className="badge bg-warning text-dark">
                                                                Planned
                                                            </span>

                                                        )}

                                                    </td>


                                                    {/* ACTION
                                                    ================= */}

                                                    <td className="text-center">

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-warning me-2"
                                                            onClick={() =>
                                                                onEdit(
                                                                    setup
                                                                )
                                                            }
                                                        >
                                                            Edit
                                                        </button>


                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() =>
                                                                onDelete(
                                                                    setup
                                                                )
                                                            }
                                                        >
                                                            Delete
                                                        </button>

                                                    </td>

                                                </tr>

                                            );

                                        }
                                    )}

                                </tbody>

                            </table>

                        </div>


                        {/* =================================================
                            FOOTER
                        ================================================= */}

                        <div className="d-flex justify-content-between align-items-center mt-1 flex-wrap gap-1">


                            {/* RECORD INFORMATION */}

                            <div className="text-muted">

                                Showing{" "}

                                {
                                    (
                                        (currentPage - 1)
                                        * pageSize
                                    ) + 1
                                }

                                {" "}to{" "}

                                {Math.min(
                                    currentPage * pageSize,
                                    filteredAndSortedSetups.length
                                )}

                                {" "}of{" "}

                                {filteredAndSortedSetups.length}

                                {" "}entries

                            </div>


                            {/* PAGINATION */}

                            {totalPages > 1 && (

                                <nav>

                                    <ul className="pagination pagination-sm mb-0">


                                        {/* PREVIOUS */}

                                        <li
                                            className={`page-item ${
                                                currentPage === 1
                                                    ? "disabled"
                                                    : ""
                                            }`}
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                onClick={() =>
                                                    handlePageChange(
                                                        currentPage - 1
                                                    )
                                                }
                                                disabled={
                                                    currentPage === 1
                                                }
                                            >
                                                Previous
                                            </button>

                                        </li>


                                        {/* PAGE NUMBERS */}

                                        {pageNumbers.map(
                                            (page) => (

                                                <li
                                                    key={page}
                                                    className={`page-item ${
                                                        currentPage === page
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >

                                                    <button
                                                        type="button"
                                                        className="page-link"
                                                        onClick={() =>
                                                            handlePageChange(
                                                                page
                                                            )
                                                        }
                                                    >
                                                        {page}
                                                    </button>

                                                </li>

                                            )
                                        )}


                                        {/* NEXT */}

                                        <li
                                            className={`page-item ${
                                                currentPage === totalPages
                                                    ? "disabled"
                                                    : ""
                                            }`}
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                onClick={() =>
                                                    handlePageChange(
                                                        currentPage + 1
                                                    )
                                                }
                                                disabled={
                                                    currentPage ===
                                                    totalPages
                                                }
                                            >
                                                Next
                                            </button>

                                        </li>

                                    </ul>

                                </nav>

                            )}

                        </div>

                    </>

                )}

            </div>

        </div>

    );

};

export default PlanShiftList;

